import { createFileRoute, Link } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { Flame } from "lucide-react";

/**
 * The privacy statement.
 *
 * It exists because measurement obliges it: Google's customer data policy
 * requires the sharing of a signed-in visitor's address for ad measurement to
 * be disclosed where visitors can read it.
 */

const TITLE = "Privacy — SyncAni";
const DESCRIPTION =
  "What SyncAni stores, what it measures, and what is shared with Google for ad measurement when you consent.";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PrivacyRoute,
});

function Section({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h2 className="text-base font-semibold text-foreground">{heading}</h2>
      <div className="space-y-2 text-sm text-muted-foreground">{children}</div>
    </section>
  );
}

function PrivacyRoute() {
  const { t } = useTranslation();

  return (
    <main className="min-h-svh bg-background px-4 py-10">
      <div className="mx-auto w-full max-w-2xl space-y-8">
        <header className="space-y-3">
          <Link
            to="/"
            search={{ enter: undefined, room: undefined }}
            aria-label={t("app.home")}
            className="flex items-center gap-2 text-foreground"
          >
            <Flame className="h-6 w-6 text-primary" aria-hidden="true" />
            <span className="text-lg font-semibold tracking-tight">
              {t("app.name")}
            </span>
          </Link>
          <h1 className="text-xl font-semibold text-foreground">
            {t("privacy.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("privacy.summary")}
          </p>
        </header>

        <Section heading={t("privacy.headingConversations")}>
          <p>{t("privacy.media")}</p>
        </Section>

        <Section heading={t("privacy.headingNecessary")}>
          <p>{t("privacy.necessary")}</p>
        </Section>

        <Section heading={t("privacy.headingAnalytics")}>
          <p>{t("privacy.analytics")}</p>
        </Section>

        <Section heading={t("privacy.headingAdvertising")}>
          <p>{t("privacy.advertising")}</p>
          <p>{t("privacy.email")}</p>
          <p>{t("privacy.decline")}</p>
        </Section>

        <Section heading={t("privacy.headingChangingMind")}>
          <p>
            <Trans
              i18nKey="privacy.reopen"
              components={[<span key="0" className="font-medium" />]}
            />
          </p>
        </Section>

        <p>
          <Link
            to="/"
            search={{ enter: undefined, room: undefined }}
            className="text-xs text-muted-foreground underline-offset-4 hover:underline"
          >
            {t("action.back")}
          </Link>
        </p>
      </div>
    </main>
  );
}
